import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        api.get('/sms/stats'),
        api.get('/sms/history')
      ]);
      setStats(statsRes.data.stats);
      setRecentMessages(historyRes.data.messages.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      sent: '#228b22',
      scheduled: '#ffd700',
      failed: '#c41e3a',
      pending: '#a8b5c8',
      cancelled: '#666'
    };
    return colors[status] || '#a8b5c8';
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="hero" style={{ padding: '30px 20px' }}>
        <h1 className="hero-title" style={{ fontSize: '2.5rem' }}>
          🎄 Welcome, {user?.name}!
        </h1>
        <p className="hero-subtitle">
          Ready to spread some Christmas cheer?
        </p>
      </div>

      {/* Credits Alert */}
      {user?.credits === 0 && (
        <div className="alert alert-info">
          <span>🎁</span>
          <span>You have no credits. Purchase credits to start sending magical messages!</span>
          <Link to="/credits" className="btn btn-gold" style={{ marginLeft: 'auto' }}>
            Buy Credits
          </Link>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{user?.credits || 0}</div>
          <div className="stat-label">Credits Available</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.total || 0}</div>
          <div className="stat-label">Total Messages</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.sent || 0}</div>
          <div className="stat-label">Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.scheduled || 0}</div>
          <div className="stat-label">Scheduled</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="card-title">🎅 Quick Actions</h2>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <Link to="/send" className="btn btn-primary">
            ✉️ Send New Message
          </Link>
          <Link to="/credits" className="btn btn-secondary">
            🎁 Buy Credits
          </Link>
          <Link to="/history" className="btn btn-outline">
            📜 View All Messages
          </Link>
        </div>
      </div>

      {/* Recent Messages */}
      <div className="card">
        <h2 className="card-title">📬 Recent Messages</h2>
        {recentMessages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: '3rem', marginBottom: '10px' }}>📭</p>
            <p>No messages yet. Send your first festive message!</p>
            <Link to="/send" className="btn btn-primary" style={{ marginTop: '15px' }}>
              Send First Message
            </Link>
          </div>
        ) : (
          <div className="message-list">
            {recentMessages.map(message => (
              <div key={message.id} className="message-item">
                <div 
                  className={`message-status ${message.status}`}
                  title={message.status}
                ></div>
                <div className="message-details">
                  <div className="message-recipient">
                    {message.senderId} → {message.recipientPhone}
                  </div>
                  <div className="message-content">{message.content}</div>
                </div>
                <div className="message-time">
                  <div style={{ color: getStatusColor(message.status), fontWeight: 600 }}>
                    {message.status.toUpperCase()}
                  </div>
                  <div>{formatDate(message.sentAt || message.scheduledAt || message.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
